class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.cachedResults = {};
    this.input = this.querySelector('input[type="search"]');
    this.predictiveSearchResults = this.querySelector(
      "[data-predictive-search]",
    );
    this.isOpen = false;

    this.setupEventListeners();
    this.onViewportChange = () => {
      if (this.isOpen)
        this.predictiveSearchResults.style.maxHeight = `${this.getResultsMaxHeight()}px`;
    };
  }

  connectedCallback() {
    window.addEventListener("resize", this.onViewportChange);
  }

  disconnectedCallback() {
    window.removeEventListener("resize", this.onViewportChange);
  }

  setupEventListeners() {
    const form = this.querySelector("form.search");

    if (!form) {
      return;
    }

    form.addEventListener("submit", this.onFormSubmit.bind(this));
    this.select = this.querySelector("select");

    this.input.addEventListener(
      "input",
      debounce((event) => {
        this.onChange(event);
      }, 300).bind(this),
    );

    if (this.select) {
      this.select.addEventListener(
        "input",
        debounce((event) => {
          this.onChange(event);
        }, 300).bind(this),
      );
    }

    this.input.addEventListener("focus", this.onFocus.bind(this));
    this.addEventListener("focusout", this.onFocusOut.bind(this));
    this.addEventListener("keyup", this.onKeyup.bind(this));
    this.addEventListener("keydown", this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  getSearchFilterType() {
    if (!this.select) {
      return "";
    }

    return this.select.dataset.type;
  }

  getSearchFilterTypeQuery() {
    if (!this.select) {
      return "";
    }

    return this.select.value.trim();
  }

  onChange() {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) {
      this.currentQueryKey = null;
      this.close(true);
      this.removeAttribute("loading");
      return;
    }

    this.getSearchResults(searchTerm);
  }

  onFormSubmit(event) {
    // Enter on an active option is handled in onKeydown/onKeyup. A deliberate
    // click on either search button must still submit the full native search.
    if (!this.getQuery().length) event.preventDefault();
  }

  onFocus() {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) {
      return;
    }

    // Reuse only the cache for the current query, never a previous term's rendered panel.
    this.getSearchResults(searchTerm);
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) {
        this.close();
      }
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) {
      this.close(true);
    }

    event.preventDefault();

    switch (event.code) {
      case "ArrowUp":
        this.switchOption("up");
        break;
      case "ArrowDown":
        this.switchOption("down");
        break;
      case "Enter":
        this.selectOption();
        break;
      case "Escape":
        this.close();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (
      event.code === "ArrowUp" ||
      event.code === "ArrowDown" ||
      (event.code === "Escape" && this.isOpen) ||
      (event.code === "Enter" && this.querySelector('[aria-selected="true"]'))
    ) {
      event.preventDefault();
    }
  }

  switchOption(direction) {
    if (!this.getAttribute("open")) {
      return;
    }

    const moveUp = direction === "up";
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(
      this.querySelectorAll('[role="option"]'),
    ).filter((element) => element.offsetParent !== null);
    const selectedElementIndex = allVisibleElements.indexOf(selectedElement);
    if (selectedElement && selectedElementIndex === -1) {
      selectedElement.setAttribute("aria-selected", false);
      this.input.setAttribute("aria-activedescendant", "");
    }
    if (!allVisibleElements.length || (moveUp && selectedElementIndex === -1))
      return;

    this.statusElement =
      this.statusElement || this.querySelector(".predictive-search-status");
    if (this.statusElement) this.statusElement.textContent = "";

    const activeElementIndex =
      (selectedElementIndex + (moveUp ? -1 : 1) + allVisibleElements.length) %
      allVisibleElements.length;
    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute("aria-selected", true);
    if (selectedElement) selectedElement.setAttribute("aria-selected", false);

    this.input.setAttribute("aria-activedescendant", activeElement.id);
    activeElement.scrollIntoView({ block: "nearest" });
  }

  selectOption() {
    const selectedProduct = this.querySelector(
      '[aria-selected="true"] a, [aria-selected="true"] button',
    );

    if (selectedProduct) {
      selectedProduct.click();
    }
  }

  getSearchResults(searchTerm) {
    const filterType = this.getSearchFilterType();
    const filterTypeQuery = this.getSearchFilterTypeQuery();
    // Keep punctuation and spaces distinct; SKUs such as AB-12 and AB 12 must not share cached results.
    const queryKey = JSON.stringify([
      searchTerm.toLowerCase(),
      filterType,
      filterTypeQuery.toLowerCase(),
    ]);

    this.currentQueryKey = queryKey;
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    const limit = this.dataset.limit;
    const limitScope = this.dataset.limitScope;
    const query = filterTypeQuery
      ? encodeURIComponent(`${filterType}:${filterTypeQuery}+* ${searchTerm}`)
      : encodeURIComponent(searchTerm);

    return fetch(
      `${routes.predictive_search_url}?q=${query}&resources[limit_scope]=${limitScope}&resources[limit]=${limit}&options[prefix]=last&section_id=predictive-search&resources[options][fields]=title,body,product_type,tag,variants.title,variants.sku,vendor`,
    )
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const resultsSection = new DOMParser()
          .parseFromString(text, "text/html")
          .querySelector("#shopify-section-predictive-search");
        if (!resultsSection)
          throw new Error("Missing predictive search section");
        const resultsMarkup = resultsSection.innerHTML;
        this.cachedResults[queryKey] = resultsMarkup;
        // A slower previous query must not overwrite a newer term or reopen a cleared search.
        if (this.currentQueryKey === queryKey)
          this.renderSearchResults(resultsMarkup);
      })
      .catch(() => {
        if (this.currentQueryKey !== queryKey) return;
        this.removeAttribute("loading");
        this.removeAttribute("results");
        this.close();
        // Keep the native search form available when suggestions cannot be fetched.
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement =
      this.statusElement || this.querySelector(".predictive-search-status");
    this.loadingText =
      this.loadingText || this.getAttribute("data-loading-text");

    this.setLiveRegionText(this.loadingText);
    this.setAttribute("loading", true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute("aria-hidden", "false");
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute("aria-hidden", "true");
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute("results", true);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute("loading");
    this.setLiveRegionText(
      this.querySelector("[data-predictive-search-live-region-count-value]")
        .textContent,
    );
  }

  getResultsMaxHeight() {
    const anchor =
      this.querySelector(".header-search-global-form-wrap") || this.input;
    const bottom = anchor.getBoundingClientRect().bottom;
    this.resultsMaxHeight = Math.max(
      0,
      Math.min(560, window.innerHeight - bottom - 12),
    );
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = `${this.getResultsMaxHeight()}px`;
    this.setAttribute("open", true);
    this.input.setAttribute("aria-expanded", true);

    if (!this.isOpen) {
      const event = new CustomEvent("search:open");
      document.dispatchEvent(event);
    }

    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    // Keep any pending response cacheable, but do not let it reopen a dismissed panel.
    this.currentQueryKey = null;
    this.removeAttribute("loading");
    if (clearSearchTerm) {
      this.input.value = "";
      this.removeAttribute("results");
    }

    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) {
      selected.setAttribute("aria-selected", false);
    }

    this.input.setAttribute("aria-activedescendant", "");
    this.removeAttribute("open");
    this.input.setAttribute("aria-expanded", false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute("style");

    if (this.isOpen) {
      const event = new CustomEvent("search:close");
      document.dispatchEvent(event);
    }

    this.isOpen = false;
  }
}

customElements.define("predictive-search", PredictiveSearch);
